// components/AdminPanel.jsx
import React, { useState, useEffect, useRef } from 'react';
import './styles/AdminPanel.css';
import config from '../configs/config';
import apiService from '../services/apiService';
import {
  FaUsers, FaBuilding, FaProjectDiagram,
  FaEdit, FaTimes, FaPlus, FaSearch, FaSpinner,
  FaShareAlt, FaArrowLeft, FaEnvelope,
  FaToggleOn, FaToggleOff, FaCode, FaDatabase,
  FaUserPlus, FaLock, FaUser, FaTrash, FaEye, FaEyeSlash,
  FaCheckCircle
} from 'react-icons/fa';

// 'pulsar' is internal-only: the page exposes every client's project names and
// sender addresses, so leave it off for client organizations.
const FEATURE_KEYS = ['home', 'scripting', 'datacheck', 'datatables', 'banner', 'weighting', 'dataanalysis', 'dashboard', 'qnrdesign', 'orgadmin', 'pulsar'];

const SCRIPTING_FEATURE_KEYS = [
  'global_settings', 'quota_settings', 'redirects_settings', 'custom_css_js', 'psnst', 'all_lists', 'actions', 'history', 'warnings', 'preview',
  'save_survey', 'add_to_tables',
  // export
  'sawtooth', 'pollstudio', 'confirmit', 'decipher_xml', 'decipher', 'dimension', 'qualtrics', 'intelli',
];

const DATATABLES_FEATURE_KEYS = [
  'append_variable', 'auto_refresh', 'banner', 'bilendi_setup', 'dimension', 'export_excel',
  'export_type', 'import_excel', 'import_oe', 'json_ai', 'properties', 'quantum',
  'run_tables', 'scripting', 'select_banner', 'select_json', 'select_weights',
  'sig_test', 'survation_format', 'toluna_format', 'tab_specs', 'table_json', 'uncle',
  'update_locations', 'wincross'
];

// Only the formats actually gated by a hasDownloadAccess() call in
// SurveyBuilder's single-question export toolbar - sawtooth/pollstudio/
// confirmit/decipher/intelli are accepted by the org model but nothing in
// the UI currently checks for them, so they're left out here.
const DOWNLOAD_FEATURE_OPTIONS = ['decipher_xml', 'dimension', 'qualtrics'];

// Crosstab export engines gated by DataTables' allowedExports - same
// allow-all-when-empty fallback as above.
const DATATABLES_EXPORT_OPTIONS = ['uncle', 'quantum', 'wincross', 'dimension'];

const emptyProjectRow = () => ({ project_id: '', project_name: '', access_level: 'view_download' });

// Drives what a custom user can do with this project inside PollStudio
// Admin - each level is a superset of the previous one.
const ACCESS_LEVEL_OPTIONS = [
  { value: 'view', label: 'View only' },
  { value: 'view_download', label: 'View + Download' },
  { value: 'qa_edit', label: 'QA Edit' },
];

const AdminPanel = () => {
  const [organizations, setOrganizations] = useState([]);
  const [totalStats, setTotalStats] = useState({ users: 0, projects: 0, shares: 0 });
  const [loading, setLoading] = useState(true);

  const [view, setView] = useState('list');
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgUsers, setOrgUsers] = useState([]);
  const [orgProjects, setOrgProjects] = useState([]);
  const [orgDetailTab, setOrgDetailTab] = useState('users');
  const [searchTerm, setSearchTerm] = useState('');

  const [initialUserCount, setInitialUserCount] = useState(0);
  const [initialProjectCount, setInitialProjectCount] = useState(0);
  const [orgUsersLoading, setOrgUsersLoading] = useState(false);
  const [orgProjectsLoading, setOrgProjectsLoading] = useState(false);

  // ── Host 2 (PG_DB_HOST2) ────────────────────────────────────
  // A fully separate section with its own view/nav state - never mixed
  // into the primary org/project views/state above, since PG_DB_HOST2
  // has its own id space and is reached via its own dedicated endpoints
  // (see /api/admin/secondary/*).
  const [secondaryAvailable, setSecondaryAvailable] = useState(false);
  const [secondaryStep, setSecondaryStep] = useState('orgs'); // 'orgs' | 'org' | 'project'
  const [secondaryStats, setSecondaryStats] = useState([]);
  const [secondaryStatsLoading, setSecondaryStatsLoading] = useState(false);
  const [secondarySelectedOrg, setSecondarySelectedOrg] = useState(null);
  const [secondaryOrgTab, setSecondaryOrgTab] = useState('users'); // 'users' | 'projects' - within secondaryStep 'org'
  const [secondaryOrgUsers, setSecondaryOrgUsers] = useState([]);
  const [secondaryOrgUsersLoading, setSecondaryOrgUsersLoading] = useState(false);
  const [secondaryProjects, setSecondaryProjects] = useState([]);
  const [secondaryProjectsLoading, setSecondaryProjectsLoading] = useState(false);
  const [secondarySelectedProject, setSecondarySelectedProject] = useState(null);
  const [secondaryProjectUsers, setSecondaryProjectUsers] = useState([]);
  const [secondaryProjectUsersLoading, setSecondaryProjectUsersLoading] = useState(false);
  const [secondarySearchTerm, setSecondarySearchTerm] = useState('');

  const [selectedUser, setSelectedUser] = useState(null);
  const [userProjects, setUserProjects] = useState([]);
  const [userProjectsLoading, setUserProjectsLoading] = useState(false);

  const [selectedProject, setSelectedProject] = useState(null);
  const [projectUsers, setProjectUsers] = useState([]);
  const [projectUsersLoading, setProjectUsersLoading] = useState(false);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showScriptingModal, setShowScriptingModal] = useState(false);
  const [showDataTablesModal, setShowDataTablesModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // ── Custom User state ──────────────────────────────────────
  const [showCustomUserModal, setShowCustomUserModal] = useState(false);
  const [customUserForm, setCustomUserForm] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    projects: []  // start empty
  });
  const [customUserErrors, setCustomUserErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [customUserSuccess, setCustomUserSuccess] = useState(false);

  // ── Org custom users list (inside org detail tab) ──────────
  const [customUsers, setCustomUsers] = useState([]);
  const [customUsersLoading, setCustomUsersLoading] = useState(false);
  const [editingCustomUser, setEditingCustomUser] = useState(null);
  const [showEditCustomUserModal, setShowEditCustomUserModal] = useState(false);

  const createEmptyScriptingFeatures = () =>
    Object.fromEntries(SCRIPTING_FEATURE_KEYS.map(k => [k, false]));

  const createEmptyDataTablesFeatures = () =>
    Object.fromEntries(DATATABLES_FEATURE_KEYS.map(k => [k, false]));

  const emptyOrg = {
    name: '', domain: '', logo_url: '', favicon_url: '',
    features: { home: true, scripting: true, datacheck: true, datatables: true, banner: true, weighting: true, dataanalysis: true, dashboard: true, qnrdesign: false, orgadmin: false },
    scripting_feature: createEmptyScriptingFeatures(),
    data_tables_feature: createEmptyDataTablesFeatures(),
    download_features: [...DOWNLOAD_FEATURE_OPTIONS],
    datatables_exports: [...DATATABLES_EXPORT_OPTIONS]
  };

  const [newOrg, setNewOrg] = useState(emptyOrg);
  const [editOrg, setEditOrg] = useState({
    id: null, name: '', domain: '', logo_url: '', favicon_url: '',
    features: {}, scripting_feature: {}, data_tables_feature: {},
    download_features: [], datatables_exports: []
  });

  const h = () => {
    const token = apiService.getToken();
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  const formatUploadTime = (iso) => {
    if (!iso) return 'N/A';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return 'N/A';
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  };

  useEffect(() => { loadAll(); checkSecondaryAvailable(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${config.API_BASE_URL}/api/admin/organizations`, { headers: h(), credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setOrganizations(data);
        setTotalStats({
          users: data.reduce((s, o) => s + (o.user_count || 0), 0),
          projects: data.reduce((s, o) => s + (o.project_count || 0), 0),
          shares: 0
        });
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  // Checked once so the standalone Host 2 button/view only shows up on
  // deployments where PG_DB_HOST2 is actually configured.
  const checkSecondaryAvailable = async () => {
    try {
      const res = await fetch(`${config.API_BASE_URL}/api/admin/secondary/status`, { headers: h(), credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSecondaryAvailable(!!data.configured);
      }
    } catch (e) { setSecondaryAvailable(false); }
  };

  // ── Host 2 (PG_DB_HOST2) standalone view ────────────────────
  // Separate nav stack from the primary org/project views above - entered
  // via its own button, never nested inside the primary org drill-down.
  const fetchSecondaryStats = async () => {
    setSecondaryStatsLoading(true);
    try {
      const res = await fetch(`${config.API_BASE_URL}/api/admin/secondary/stats`, { headers: h(), credentials: 'include' });
      const data = res.ok ? await res.json() : { organizations: [] };
      setSecondaryStats(data.organizations || []);
    } catch (e) { setSecondaryStats([]); }
    finally { setSecondaryStatsLoading(false); }
  };

  const fetchSecondaryOrgUsers = async (orgId) => {
    setSecondaryOrgUsersLoading(true);
    try {
      const res = await fetch(`${config.API_BASE_URL}/api/admin/secondary/organizations/${orgId}/users`, { headers: h(), credentials: 'include' });
      setSecondaryOrgUsers(res.ok ? await res.json() : []);
    } catch (e) { setSecondaryOrgUsers([]); }
    finally { setSecondaryOrgUsersLoading(false); }
  };

  const fetchSecondaryProjects = async (orgId) => {
    setSecondaryProjectsLoading(true);
    try {
      const res = await fetch(`${config.API_BASE_URL}/api/admin/secondary/organizations/${orgId}/projects`, { headers: h(), credentials: 'include' });
      setSecondaryProjects(res.ok ? await res.json() : []);
    } catch (e) { setSecondaryProjects([]); }
    finally { setSecondaryProjectsLoading(false); }
  };

  const fetchSecondaryProjectUsers = async (projectId) => {
    setSecondaryProjectUsersLoading(true);
    try {
      const res = await fetch(`${config.API_BASE_URL}/api/admin/secondary/projects/${projectId}/users`, { headers: h(), credentials: 'include' });
      setSecondaryProjectUsers(res.ok ? await res.json() : []);
    } catch (e) { setSecondaryProjectUsers([]); }
    finally { setSecondaryProjectUsersLoading(false); }
  };

  const openSecondaryView = async () => {
    setView('secondary');
    setSecondaryStep('orgs');
    setSecondarySelectedOrg(null);
    setSecondarySelectedProject(null);
    setSecondarySearchTerm('');
    await fetchSecondaryStats();
  };

  const openSecondaryOrg = async (org) => {
    setSecondarySelectedOrg(org);
    setSecondaryStep('org');
    setSecondaryOrgTab('users');
    setSecondarySearchTerm('');
    setSecondaryOrgUsers([]); setSecondaryProjects([]);
    await Promise.all([fetchSecondaryOrgUsers(org.id), fetchSecondaryProjects(org.id)]);
  };

  const openSecondaryProject = async (project) => {
    setSecondarySelectedProject(project);
    setSecondaryStep('project');
    await fetchSecondaryProjectUsers(project.id);
  };

  const secondaryGoBack = () => {
    if (secondaryStep === 'project') { setSecondarySelectedProject(null); setSecondaryProjectUsers([]); setSecondaryStep('org'); }
    else if (secondaryStep === 'org') { setSecondarySelectedOrg(null); setSecondaryOrgUsers([]); setSecondaryProjects([]); setSecondarySearchTerm(''); setSecondaryStep('orgs'); }
    else { setView('list'); }
  };

  const fetchOrgUsers = async (orgId) => {
    setOrgUsersLoading(true);
    try {
      const res = await fetch(`${config.API_BASE_URL}/api/admin/organizations/${orgId}/users`, { headers: h(), credentials: 'include' });
      setOrgUsers(res.ok ? await res.json() : []);
    } catch (e) { setOrgUsers([]); }
    finally { setOrgUsersLoading(false); }
  };

  const fetchOrgProjects = async (orgId) => {
    setOrgProjectsLoading(true);
    try {
      const res = await fetch(`${config.API_BASE_URL}/api/admin/organizations/${orgId}/projects`, { headers: h(), credentials: 'include' });
      setOrgProjects(res.ok ? await res.json() : []);
    } catch (e) { setOrgProjects([]); }
    finally { setOrgProjectsLoading(false); }
  };

  const fetchOrgCustomUsers = async (orgId) => {
    setCustomUsersLoading(true);
    try {
      const res = await fetch(`${config.API_BASE_URL}/api/admin/organizations/${orgId}/custom-users`, { headers: h(), credentials: 'include' });
      setCustomUsers(res.ok ? await res.json() : []);
    } catch (e) { setCustomUsers([]); }
    finally { setCustomUsersLoading(false); }
  };

  const fetchUserProjects = async (email, orgId) => {
    setUserProjectsLoading(true);
    try {
      const res = await fetch(`${config.API_BASE_URL}/api/admin/organizations/${orgId}/users/${encodeURIComponent(email)}/projects`, { headers: h(), credentials: 'include' });
      const data = res.ok ? await res.json() : [];
      setUserProjects(data.map(p => ({ ...p, shared_with_count: p.shared_with_count || 0, role: p.is_shared ? 'shared' : 'owner' })));
    } catch (e) { setUserProjects([]); }
    finally { setUserProjectsLoading(false); }
  };

  const fetchProjectUsers = async (projectId) => {
    setProjectUsersLoading(true);
    try {
      const res = await fetch(`${config.API_BASE_URL}/api/admin/projects/${projectId}/users`, { headers: h(), credentials: 'include' });
      setProjectUsers(res.ok ? await res.json() : []);
    } catch (e) { setProjectUsers([]); }
    finally { setProjectUsersLoading(false); }
  };

  const openOrg = async (org) => {
    setInitialUserCount(org.user_count || 0);
    setInitialProjectCount(org.project_count || 0);
    setSelectedOrg(org);
    setOrgDetailTab('users');
    setSearchTerm('');
    setView('org');
    setOrgUsers([]); setOrgProjects([]); setCustomUsers([]);
    await Promise.all([fetchOrgUsers(org.id), fetchOrgProjects(org.id), fetchOrgCustomUsers(org.id)]);
  };

  const openUser = async (user) => {
    setSelectedUser(user);
    setView('user');
    await fetchUserProjects(user.email, selectedOrg.id);
  };

  const openProject = async (project) => {
    setSelectedProject(project);
    setView('project');
    await fetchProjectUsers(project.id);
  };

  const goBack = () => {
    if (view === 'user') { setSelectedUser(null); setUserProjects([]); setView('org'); setOrgDetailTab('users'); }
    if (view === 'project') { setSelectedProject(null); setProjectUsers([]); setView('org'); setOrgDetailTab('projects'); }
    if (view === 'org') { setSelectedOrg(null); setOrgUsers([]); setOrgProjects([]); setCustomUsers([]); setInitialUserCount(0); setInitialProjectCount(0); setView('list'); }
  };

  const handleAdd = async () => {
    if (!newOrg.name || !newOrg.domain) { alert('Name aur domain required hai'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${config.API_BASE_URL}/api/admin/organizations`, { method: 'POST', headers: h(), credentials: 'include', body: JSON.stringify(newOrg) });
      if (res.ok) { setShowAddModal(false); setNewOrg(emptyOrg); await loadAll(); }
      else { const e = await res.json(); alert(e.error || 'Failed'); }
    } catch (e) { alert('Failed'); }
    finally { setSubmitting(false); }
  };

  const handleEdit = async () => {
    if (!editOrg.name || !editOrg.domain) { alert('Name aur domain required hai'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${config.API_BASE_URL}/api/admin/organizations/${editOrg.id}`, {
        method: 'PUT', headers: h(), credentials: 'include',
        body: JSON.stringify({ name: editOrg.name, domain: editOrg.domain, logo_url: editOrg.logo_url, favicon_url: editOrg.favicon_url, features: editOrg.features, scripting_feature: editOrg.scripting_feature, data_tables_feature: editOrg.data_tables_feature, download_features: editOrg.download_features, datatables_exports: editOrg.datatables_exports })
      });
      if (res.ok) {
        setShowEditModal(false);
        await loadAll();
        if (selectedOrg?.id === editOrg.id) setSelectedOrg(prev => ({ ...prev, ...editOrg }));
      } else { const e = await res.json(); alert(e.error || 'Failed'); }
    } catch (e) { alert('Failed'); }
    finally { setSubmitting(false); }
  };

  // ── Custom User: validation ────────────────────────────────
  const validateCustomUser = (form) => {
    const errs = {};

    // Email validation
    if (!form.username.trim()) errs.username = 'Email required';
    else if (form.username.trim().length < 3) errs.username = 'Min 3 characters';
    else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(form.username.trim())) errs.username = 'Please enter a valid email address';

    if (!form.password) errs.password = 'Password required';
    else if (form.password.length < 6) errs.password = 'Min 6 characters';

    if (!form.confirmPassword) errs.confirmPassword = 'Please confirm password';
    else if (form.password !== form.confirmPassword) errs.confirmPassword = 'Passwords do not match';

    // Projects validation: at least one valid project
    const validProjects = form.projects.filter(p => p.project_id.trim() && p.project_name.trim());
    if (validProjects.length === 0) {
      errs.projects = 'Please select at least one project';
    }

    return errs;
  };

  const handleAddCustomUser = async () => {
    const errs = validateCustomUser(customUserForm);
    setCustomUserErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSubmitting(true);
    try {
      const projects = customUserForm.projects.filter(p => p.project_id.trim() && p.project_name.trim());
      const payload = {
        username: customUserForm.username.trim(),
        password: customUserForm.password,
        org_id: selectedOrg.id,
        projects
      };
      const res = await fetch(`${config.API_BASE_URL}/api/admin/custom-users`, { method: 'POST', headers: h(), credentials: 'include', body: JSON.stringify(payload) });
      if (res.ok) {
        setCustomUserSuccess(true);
        setTimeout(() => {
          setCustomUserSuccess(false);
          setShowCustomUserModal(false);
          resetCustomUserForm();
          fetchOrgCustomUsers(selectedOrg.id);
        }, 1500);
      } else {
        const e = await res.json();
        setCustomUserErrors({ api: e.error || 'Failed to create user' });
      }
    } catch (e) { setCustomUserErrors({ api: 'Network error' }); }
    finally { setSubmitting(false); }
  };

  const handleDeleteCustomUser = async (userId) => {
    if (!window.confirm('Delete this custom user?')) return;
    try {
      const res = await fetch(`${config.API_BASE_URL}/api/admin/custom-users/${userId}`, { method: 'DELETE', headers: h(), credentials: 'include' });
      if (res.ok) fetchOrgCustomUsers(selectedOrg.id);
      else alert('Delete failed');
    } catch (e) { alert('Delete failed'); }
  };

  const handleEditCustomUser = async () => {
    const errs = {};
    if (!editingCustomUser.username?.trim()) errs.username = 'Email required';
    else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(editingCustomUser.username.trim())) errs.username = 'Please enter a valid email address';
    if (editingCustomUser.newPassword && editingCustomUser.newPassword.length < 6) errs.newPassword = 'Min 6 characters';
    if (editingCustomUser.newPassword && editingCustomUser.newPassword !== editingCustomUser.confirmPassword) errs.confirmPassword = 'Passwords do not match';

    // Projects validation
    const validProjects = (editingCustomUser.projects || []).filter(p => p.project_id?.trim() && p.project_name?.trim());
    if (validProjects.length === 0) {
      errs.projects = 'Please select at least one project';
    }

    setCustomUserErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSubmitting(true);
    try {
      const projects = validProjects;
      const payload = {
        username: editingCustomUser.username.trim(),
        ...(editingCustomUser.newPassword ? { password: editingCustomUser.newPassword } : {}),
        projects
      };
      const res = await fetch(`${config.API_BASE_URL}/api/admin/custom-users/${editingCustomUser.id}`, { method: 'PUT', headers: h(), credentials: 'include', body: JSON.stringify(payload) });
      if (res.ok) {
        setShowEditCustomUserModal(false);
        setEditingCustomUser(null);
        setCustomUserErrors({});
        fetchOrgCustomUsers(selectedOrg.id);
      } else { const e = await res.json(); setCustomUserErrors({ api: e.error || 'Update failed' }); }
    } catch (e) { setCustomUserErrors({ api: 'Network error' }); }
    finally { setSubmitting(false); }
  };

  const resetCustomUserForm = () => {
    setCustomUserForm({ username: '', password: '', confirmPassword: '', projects: [] });
    setCustomUserErrors({});
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  // ── Toggle helpers ─────────────────────────────────────────
  const toggleMainFeature = (key, setFn) => setFn(prev => ({ ...prev, features: { ...prev.features, [key]: !prev.features[key] } }));
  const toggleScriptingFeature = (key, setFn) => setFn(prev => ({ ...prev, scripting_feature: { ...prev.scripting_feature, [key]: !prev.scripting_feature[key] } }));
  const toggleDataTablesFeature = (key, setFn) => setFn(prev => ({ ...prev, data_tables_feature: { ...prev.data_tables_feature, [key]: !prev.data_tables_feature[key] } }));
  const toggleExport = (key, field, setFn) => setFn(prev => ({ ...prev, [field]: prev[field].includes(key) ? prev[field].filter(x => x !== key) : [...prev[field], key] }));

  const filteredUsers = orgUsers.filter(u => u.email?.toLowerCase().includes(searchTerm.toLowerCase()) || u.full_name?.toLowerCase().includes(searchTerm.toLowerCase()));
  const filteredProjects = orgProjects.filter(p => p.project_name?.toLowerCase().includes(searchTerm.toLowerCase()));
  const filteredCustomUsers = customUsers.filter(u => u.username?.toLowerCase().includes(searchTerm.toLowerCase()));

  const filteredSecondaryStats = secondaryStats.filter(o => o.name?.toLowerCase().includes(secondarySearchTerm.toLowerCase()) || o.domain?.toLowerCase().includes(secondarySearchTerm.toLowerCase()));
  const filteredSecondaryOrgUsers = secondaryOrgUsers.filter(u => u.email?.toLowerCase().includes(secondarySearchTerm.toLowerCase()) || u.full_name?.toLowerCase().includes(secondarySearchTerm.toLowerCase()));
  const filteredSecondaryProjects = secondaryProjects.filter(p => p.project_name?.toLowerCase().includes(secondarySearchTerm.toLowerCase()));

  // ─── ProjectMultiSelect ──────────────────────────────────────────────────────
  const ProjectMultiSelect = ({ projects, setProjects, availableProjects = [], loading = false }) => {
    const [term, setTerm] = useState("");
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
      const handleClickOutside = (e) => {
        if (ref.current && !ref.current.contains(e.target)) {
          setOpen(false);
          setTerm("");
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const selectedIds = projects.map(p => String(p.project_id));
    const available = availableProjects.filter(p => !selectedIds.includes(String(p.id)));
    const filtered = available.filter(p =>
      term === "" ||
      p.project_name.toLowerCase().includes(term.toLowerCase()) ||
      String(p.id).includes(term)
    );

    const toggleProject = (projectId) => {
      const id = String(projectId);
      if (selectedIds.includes(id)) {
        setProjects(projects.filter(p => String(p.project_id) !== id));
      } else {
        const match = availableProjects.find(p => String(p.id) === id);
        if (match) {
          setProjects([...projects, { project_id: id, project_name: match.project_name, access_level: 'view_download' }]);
        }
      }
      setTerm("");
    };

    const removeProject = (id) => {
      setProjects(projects.filter(p => String(p.project_id) !== id));
    };

    const setProjectAccessLevel = (id, access_level) => {
      setProjects(projects.map(p => String(p.project_id) === String(id) ? { ...p, access_level } : p));
    };

    return (
      <div ref={ref} style={{ position: "relative" }}>
        <div className={`pm-multiselect-trigger ${open ? "open" : ""}`} onClick={() => setOpen(true)}>
          {projects.map(p => (
            <span key={p.project_id} className="pm-chip">
              {p.project_name}
              <select
                className="pm-chip-access-level"
                value={p.access_level || 'view_download'}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => setProjectAccessLevel(p.project_id, e.target.value)}
                title="Access level for this project"
              >
                {ACCESS_LEVEL_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <button
                className="pm-chip-remove"
                onClick={(e) => { e.stopPropagation(); removeProject(p.project_id); }}
              >×</button>
            </span>
          ))}
          <input
            className="pm-search-input"
            value={term}
            onChange={(e) => { setTerm(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder={projects.length ? "" : "Search projects…"}
          />
          <span className="pm-caret">▾</span>
        </div>

        {open && (
          <div className="pm-dropdown">
            {loading ? (
              <div className="pm-dropdown-loading"><FaSpinner className="spinner-small" /> Loading projects…</div>
            ) : filtered.length === 0 ? (
              <div className="pm-dropdown-empty">
                {term ? "No matching projects" : "No more projects available"}
              </div>
            ) : (
              filtered.map(p => (
                <div
                  key={p.id}
                  className="pm-dropdown-item"
                  onClick={() => toggleProject(p.id)}
                >
                  <div>
                    <span className="pm-dropdown-item-title">{p.project_name}</span>
                    <span className="pm-dropdown-item-id">ID: {p.id}</span>
                  </div>
                  <span className="pm-badge">Add</span>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    );
  };

  // ── Sub-components ─────────────────────────────────────────
  const ScriptingFeaturesModal = ({ isOpen, onClose, features, toggleFn, title }) => {
    if (!isOpen) return null;
    return (
      <div className="emodal-overlay" onClick={onClose}>
        <div className="emodal-content emodal-xlarge" onClick={e => e.stopPropagation()}>
          <div className="emodal-header">
            <h3><FaCode /> {title} - Scripting Features</h3>
            <button className="emodal-close" onClick={onClose}><FaTimes /></button>
          </div>
          <div className="emodal-body">
            <div className="features-grid-3col">
              {SCRIPTING_FEATURE_KEYS.map(key => (
                <div key={key} className="feature-toggle" onClick={() => toggleFn(key)}>
                  <span className="feature-name">{key.replace(/_/g, ' ')}</span>
                  {features[key] ? <FaToggleOn className="toggle-on" /> : <FaToggleOff className="toggle-off" />}
                </div>
              ))}
            </div>
          </div>
          <div className="emodal-footer">
            <button className="btn-primary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  };

  const DataTablesFeaturesModal = ({ isOpen, onClose, features, toggleFn, title }) => {
    if (!isOpen) return null;
    return (
      <div className="emodal-overlay" onClick={onClose}>
        <div className="emodal-content emodal-xlarge" onClick={e => e.stopPropagation()}>
          <div className="emodal-header">
            <h3><FaDatabase /> {title} - Data Tables Features</h3>
            <button className="emodal-close" onClick={onClose}><FaTimes /></button>
          </div>
          <div className="emodal-body">
            <div className="features-grid-3col">
              {DATATABLES_FEATURE_KEYS.map(key => (
                <div key={key} className="feature-toggle" onClick={() => toggleFn(key)}>
                  <span className="feature-name">{key.replace(/_/g, ' ')}</span>
                  {features[key] ? <FaToggleOn className="toggle-on" /> : <FaToggleOff className="toggle-off" />}
                </div>
              ))}
            </div>
          </div>
          <div className="emodal-footer">
            <button className="btn-primary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  };

  const Breadcrumb = () => (
    <div className="breadcrumb">
      <span
        className={view === 'list' ? 'bc-active' : 'bc-link'}
        onClick={() => { if (view === 'secondary') setView('list'); else if (view !== 'list') goBack(); }}
      >
        <FaBuilding /> Organizations
      </span>
      {(view === 'org' || view === 'user' || view === 'project') && (
        <><span className="bc-sep">›</span>
          <span className={view === 'org' ? 'bc-active' : 'bc-link'} onClick={() => (view === 'user' || view === 'project') && goBack()}>
            {selectedOrg?.name}
          </span></>
      )}
      {view === 'user' && (<><span className="bc-sep">›</span><span className="bc-active">{selectedUser?.full_name}</span></>)}
      {view === 'project' && (<><span className="bc-sep">›</span><span className="bc-active">{selectedProject?.project_name}</span></>)}
      {view === 'secondary' && (
        <><span className="bc-sep">›</span>
          <span className={secondaryStep === 'orgs' ? 'bc-active' : 'bc-link'} onClick={() => secondaryStep !== 'orgs' && setSecondaryStep('orgs')}>
            Host 2
          </span></>
      )}
      {view === 'secondary' && (secondaryStep === 'org' || secondaryStep === 'project') && (
        <><span className="bc-sep">›</span>
          <span className={secondaryStep === 'org' ? 'bc-active' : 'bc-link'} onClick={() => secondaryStep === 'project' && setSecondaryStep('org')}>
            {secondarySelectedOrg?.name}
          </span></>
      )}
      {view === 'secondary' && secondaryStep === 'project' && (
        <><span className="bc-sep">›</span><span className="bc-active">{secondarySelectedProject?.project_name}</span></>
      )}
    </div>
  );

  if (loading) return (
    <div className="ap-loading"><FaSpinner className="spinner" /><p>Loading...</p></div>
  );

  return (
    <div className="admin-panel">
      <div className="ap-header">
        <div>
          <Breadcrumb />
        </div>
        <div className="ap-summary">
          <div className="ap-pill"><FaBuilding /><span>{organizations.length}</span><label>Orgs</label></div>
          <div className="ap-pill"><FaUsers /><span>{totalStats.users}</span><label>Users</label></div>
          <div className="ap-pill"><FaProjectDiagram /><span>{totalStats.projects}</span><label>Projects</label></div>
        </div>
      </div>

      {/* VIEW: LIST */}
      {view === 'list' && (
        <div className="ap-body">
          <div className="ap-row">
            <h2 className="ap-section-title">All Organizations</h2>
            <div style={{ display: 'flex', gap: 10 }}>
              {secondaryAvailable && (
                <button className="btn-secondary" onClick={openSecondaryView}><FaDatabase /> Host 2 Data</button>
              )}
              <button className="btn-primary" onClick={() => setShowAddModal(true)}><FaPlus /> Add Organization</button>
            </div>
          </div>
          <div className="org-grid">
            {organizations.map(org => (
              <div key={org.id} className="org-card" onClick={() => openOrg(org)}>
                <button
                  className="org-card-edit"
                  title="Edit organization"
                  onClick={e => {
                    e.stopPropagation();
                    setEditOrg({
                      id: org.id, name: org.name, domain: org.domain, logo_url: org.logo_url || '', favicon_url: org.favicon_url || '',
                      features: org.features || {}, scripting_feature: org.scripting_feature || createEmptyScriptingFeatures(), data_tables_feature: org.data_tables_feature || createEmptyDataTablesFeatures(),
                      download_features: org.download_features?.length ? org.download_features : DOWNLOAD_FEATURE_OPTIONS,
                      datatables_exports: org.datatables_exports?.length ? org.datatables_exports : DATATABLES_EXPORT_OPTIONS
                    });
                    setShowEditModal(true);
                  }}
                >
                  <FaEdit />
                </button>

                <div className="org-card-head">
                  {org.favicon_url
                    ? <img src={org.favicon_url} alt={org.name} className="org-av" />
                    : <div className="org-av org-av-placeholder">{org.name.charAt(0)}</div>}
                  <div className="org-card-titles">
                    <div className="org-row-name">{org.name}</div>
                    <div className="org-row-domain">{org.domain}</div>
                  </div>
                </div>

                <div className="org-card-stats">
                  <div className="org-card-stat">
                    <FaUsers />
                    <span className="org-card-stat-num">{org.user_count || 0}</span>
                    <span className="org-card-stat-label">Users</span>
                  </div>
                  <div className="org-card-stat">
                    <FaProjectDiagram />
                    <span className="org-card-stat-num">{org.project_count || 0}</span>
                    <span className="org-card-stat-label">Projects</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* VIEW: SECONDARY (Host 2 / PG_DB_HOST2) - fully separate nav stack,
          never nested inside the primary org/project views above */}
      {view === 'secondary' && (
        <div className="ap-body">
          <button className="btn-primary" onClick={secondaryGoBack}><FaArrowLeft /> Back</button>

          {secondaryStep === 'orgs' && (
            <>
              <div className="ap-row" style={{ marginTop: 16 }}>
                <h2 className="ap-section-title"><FaDatabase /> Host 2 Organizations</h2>
              </div>
              <div className="search-bar">
                <FaSearch />
                <input type="text" placeholder="Search organizations..." value={secondarySearchTerm} onChange={e => setSecondarySearchTerm(e.target.value)} />
              </div>
              {secondaryStatsLoading ? (
                <div className="loading-state"><FaSpinner className="spinner" /><p>Fetching Host 2 stats...</p></div>
              ) : filteredSecondaryStats.length > 0 ? (
                <div className="org-grid">
                  {filteredSecondaryStats.map(org => (
                    <div key={org.id} className="org-card" onClick={() => openSecondaryOrg(org)}>
                      <div className="org-card-head">
                        {org.favicon_url
                          ? <img src={org.favicon_url} alt={org.name} className="org-av" />
                          : <div className="org-av org-av-placeholder">{org.name.charAt(0)}</div>}
                        <div className="org-card-titles">
                          <div className="org-row-name">{org.name}</div>
                          <div className="org-row-domain">{org.domain}</div>
                        </div>
                      </div>

                      <div className="org-card-stats org-card-stats-3col">
                        <div className="org-card-stat">
                          <FaUsers />
                          <span className="org-card-stat-num">{org.total_users || 0}</span>
                          <span className="org-card-stat-label">Users</span>
                        </div>
                        <div className="org-card-stat">
                          <FaProjectDiagram />
                          <span className="org-card-stat-num">{org.total_projects || 0}</span>
                          <span className="org-card-stat-label">Projects</span>
                        </div>
                        <div className="org-card-stat">
                          <FaShareAlt />
                          <span className="org-card-stat-num">{org.total_shares || 0}</span>
                          <span className="org-card-stat-label">Shared</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : <div className="empty-msg">No organizations have data on Host 2</div>}
            </>
          )}

          {secondaryStep === 'org' && (
            <>
              <div className="ap-row" style={{ marginTop: 16 }}>
                <h2 className="ap-section-title"><FaDatabase /> {secondarySelectedOrg?.name} - Host 2</h2>
              </div>

              <div className="detail-tabs">
                <button className={secondaryOrgTab === 'users' ? 'active' : ''} onClick={() => { setSecondaryOrgTab('users'); setSecondarySearchTerm(''); }}>
                  <FaUsers /> Users ({secondaryOrgUsersLoading ? '…' : secondaryOrgUsers.length})
                </button>
                <button className={secondaryOrgTab === 'projects' ? 'active' : ''} onClick={() => { setSecondaryOrgTab('projects'); setSecondarySearchTerm(''); }}>
                  <FaProjectDiagram /> Projects ({secondaryProjectsLoading ? '…' : secondaryProjects.length})
                </button>
              </div>

              <div className="search-bar">
                <FaSearch />
                <input type="text" placeholder={`Search ${secondaryOrgTab}...`} value={secondarySearchTerm} onChange={e => setSecondarySearchTerm(e.target.value)} />
              </div>

              {secondaryOrgTab === 'users' && (
                <div className="item-list">
                  {secondaryOrgUsersLoading ? (
                    <div className="loading-state"><FaSpinner className="spinner" /><p>Fetching Host 2 users...</p></div>
                  ) : filteredSecondaryOrgUsers.length > 0 ? (
                    filteredSecondaryOrgUsers.map(user => (
                      <div key={user.email} className="item-row">
                        <div className="user-av">{(user.full_name?.[0] || user.email?.[0] || 'U').toUpperCase()}</div>
                        <div className="item-info">
                          <div className="item-name">{user.full_name || 'Unknown'}</div>
                          <div className="item-sub"><FaEnvelope /> {user.email}</div>
                        </div>
                        <div className="item-stats">
                          <span><FaProjectDiagram /> {user.owned_projects || 0} owned</span>
                          <span><FaShareAlt /> {user.shared_projects || 0} shared</span>
                        </div>
                      </div>
                    ))
                  ) : <div className="empty-msg">No Host 2 users found</div>}
                </div>
              )}

              {secondaryOrgTab === 'projects' && (
                <div className="item-list">
                  {secondaryProjectsLoading ? (
                    <div className="loading-state"><FaSpinner className="spinner" /><p>Fetching Host 2 projects...</p></div>
                  ) : filteredSecondaryProjects.length > 0 ? (
                    filteredSecondaryProjects.map(project => (
                      <div key={project.id} className="item-row clickable" onClick={() => openSecondaryProject(project)}>
                        <div className="proj-av"><FaProjectDiagram /></div>
                        <div className="item-info">
                          <div className="item-name">
                            {project.project_name}
                            {project.id && <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--color-text-muted)', fontWeight: 'normal' }}>(ID: {project.id})</span>}
                          </div>
                          <div className="item-sub">Owner: {project.owner || 'N/A'} · DB: {project.database_name || 'N/A'} · Uploaded: {formatUploadTime(project.created_at)}</div>
                        </div>
                        <div className="item-stats">
                          <span><FaShareAlt /> {project.shared_with_count || 0} shared</span>
                        </div>
                        <span className="chevron">›</span>
                      </div>
                    ))
                  ) : <div className="empty-msg">No Host 2 projects found</div>}
                </div>
              )}
            </>
          )}

          {secondaryStep === 'project' && (
            <>
              <div className="detail-hero" style={{ marginTop: 16 }}>
                <div className="proj-av proj-av-lg"><FaProjectDiagram /></div>
                <div>
                  <h2 className="ap-section-title" style={{ margin: 0 }}>{secondarySelectedProject?.project_name}</h2>
                  <p className="org-row-domain">DB: {secondarySelectedProject?.database_name || 'N/A'} · Owner: {secondarySelectedProject?.owner || 'N/A'} · Uploaded: {formatUploadTime(secondarySelectedProject?.created_at)}</p>
                </div>
              </div>
              <h3 className="ap-sub-title">Users with Access (Host 2)</h3>
              {secondaryProjectUsersLoading ? (
                <div className="loading-state"><FaSpinner className="spinner" /><p>Loading users...</p></div>
              ) : (
                <div className="item-list">
                  {secondaryProjectUsers.length > 0 ? secondaryProjectUsers.map(u => (
                    <div key={u.email} className="item-row">
                      <div className="user-av">{(u.full_name?.[0] || u.email?.[0] || 'U').toUpperCase()}</div>
                      <div className="item-info">
                        <div className="item-name">{u.full_name || 'Unknown'}</div>
                        <div className="item-sub"><FaEnvelope /> {u.email}</div>
                      </div>
                      <div className="item-stats">
                        <span className={`role-badge ${u.role === 'owner' ? 'role-owner' : 'role-shared'}`}>
                          {u.role === 'owner' ? 'Owner' : 'Shared'}
                        </span>
                      </div>
                    </div>
                  )) : <div className="empty-msg">No users found</div>}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* VIEW: ORG DETAIL */}
      {view === 'org' && (
        <div className="ap-body">
          <button className="btn-primary" onClick={goBack}><FaArrowLeft /> Back</button>
          <div className="ap-row" style={{ marginBottom: 20 }}>
            <div className="org-detail-head">
              {selectedOrg.favicon_url
                ? <img src={selectedOrg.favicon_url} alt={selectedOrg.name} className="org-av-lg" />
                : <div className="org-av-lg org-av-placeholder">{selectedOrg.name.charAt(0)}</div>}
              <div>
                <h2 className="ap-section-title" style={{ margin: 0 }}>{selectedOrg.name}</h2>
                <p className="org-row-domain">{selectedOrg.domain}</p>
              </div>
            </div>

            {/* Add Custom User button — visible in org view */}
            <button
              className="btn-primary"
              onClick={() => { resetCustomUserForm(); setShowCustomUserModal(true); }}
            >
              <FaUserPlus /> Add Custom User
            </button>
          </div>

          <div className="detail-tabs">
            <button className={orgDetailTab === 'users' ? 'active' : ''} onClick={() => { setOrgDetailTab('users'); setSearchTerm(''); }}>
              <FaUsers /> Users ({orgUsersLoading && orgUsers.length === 0 ? initialUserCount : orgUsers.length})
            </button>
            <button className={orgDetailTab === 'projects' ? 'active' : ''} onClick={() => { setOrgDetailTab('projects'); setSearchTerm(''); }}>
              <FaProjectDiagram /> Projects ({orgProjectsLoading && orgProjects.length === 0 ? initialProjectCount : orgProjects.length})
            </button>
            <button className={orgDetailTab === 'custom_users' ? 'active' : ''} onClick={() => { setOrgDetailTab('custom_users'); setSearchTerm(''); }}>
              <FaUserPlus /> Custom Users ({customUsersLoading ? '…' : customUsers.length})
            </button>
          </div>

          <div className="search-bar">
            <FaSearch />
            <input type="text" placeholder={`Search ${orgDetailTab === 'custom_users' ? 'custom users' : orgDetailTab}...`} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
          </div>

          {/* Tab: Regular Users */}
          {orgDetailTab === 'users' && (
            <div className="item-list">
              {orgUsersLoading ? (
                <div className="loading-state"><FaSpinner className="spinner" /><p>Fetching users... (Total: {initialUserCount})</p></div>
              ) : filteredUsers.length > 0 ? (
                filteredUsers.map(user => (
                  <div key={user.email} className="item-row clickable" onClick={() => openUser(user)}>
                    <div className="user-av">{(user.full_name?.[0] || user.email?.[0] || 'U').toUpperCase()}</div>
                    <div className="item-info">
                      <div className="item-name">{user.full_name || 'Unknown'}</div>
                      <div className="item-sub"><FaEnvelope /> {user.email}</div>
                    </div>
                    <div className="item-stats">
                      <span><FaProjectDiagram /> {user.owned_projects || 0} owned</span>
                      <span><FaShareAlt /> {user.shared_projects || 0} shared</span>
                    </div>
                    <span className="chevron">›</span>
                  </div>
                ))
              ) : <div className="empty-msg">No users found</div>}
            </div>
          )}

          {/* Tab: Projects */}
          {orgDetailTab === 'projects' && (
            <div className="item-list">
              {orgProjectsLoading ? (
                <div className="loading-state"><FaSpinner className="spinner" /><p>Fetching projects... (Total: {initialProjectCount})</p></div>
              ) : filteredProjects.length > 0 ? (
                filteredProjects.map(project => (
                  <div key={project.id} className="item-row clickable" onClick={() => openProject(project)}>
                    <div className="proj-av"><FaProjectDiagram /></div>
                    <div className="item-info">
                      <div className="item-name">
                        {project.project_name}
                        {project.id && <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--color-text-muted)', fontWeight: 'normal' }}>(ID: {project.id})</span>}
                      </div>
                      <div className="item-sub">Owner: {project.owner || 'N/A'} · DB: {project.database_name || 'N/A'} · Uploaded: {formatUploadTime(project.created_at)}</div>
                    </div>
                    <div className="item-stats">
                      <span><FaShareAlt /> {project.shared_with_count || 0} shared</span>
                    </div>
                    <span className="chevron">›</span>
                  </div>
                ))
              ) : <div className="empty-msg">No projects found</div>}
            </div>
          )}

          {/* Tab: Custom Users */}
          {orgDetailTab === 'custom_users' && (
            <div className="item-list">
              {customUsersLoading ? (
                <div className="loading-state"><FaSpinner className="spinner" /><p>Loading custom users...</p></div>
              ) : filteredCustomUsers.length > 0 ? (
                filteredCustomUsers.map(cu => (
                  <div key={cu.id} className="item-row">
                    <div className="user-av custom-user-av">{(cu.username?.[0] || 'U').toUpperCase()}</div>
                    <div className="item-info">
                      <div className="item-name">
                        {cu.username}
                        <span className="badge-custom-user">Custom</span>
                      </div>
                      <div className="item-sub">
                        <FaProjectDiagram />
                        {cu.project_count > 0
                          ? `${cu.project_count} project${cu.project_count > 1 ? 's' : ''} assigned`
                          : 'No projects assigned'}
                      </div>
                    </div>
                    <div className="item-stats">
                      <span className={`role-badge ${cu.is_active ? 'role-owner' : 'role-inactive'}`}>
                        {cu.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className="cu-actions">
                      <button className="btn-icon" onClick={() => {
                        setEditingCustomUser({
                          ...cu,
                          newPassword: '',
                          confirmPassword: '',
                          projects: cu.projects?.length ? cu.projects : []
                        });
                        setCustomUserErrors({});
                        setShowEditCustomUserModal(true);
                      }}>
                        <FaEdit />
                      </button>
                      <button className="btn-icon btn-icon-danger" onClick={() => handleDeleteCustomUser(cu.id)}>
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-msg">
                  No custom users yet.{' '}
                  <span className="link-inline" onClick={() => { resetCustomUserForm(); setShowCustomUserModal(true); }}>
                    Add one
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* VIEW: USER */}
      {view === 'user' && (
        <div className="ap-body">
          <button className="btn-primary" onClick={goBack}><FaArrowLeft /> Back to Users</button>
          <div className="detail-hero">
            <div className="user-av user-av-lg">{(selectedUser.full_name?.[0] || selectedUser.email?.[0] || 'U').toUpperCase()}</div>
            <div>
              <h2 className="ap-section-title" style={{ margin: 0 }}>{selectedUser.full_name}</h2>
              <p className="org-row-domain">{selectedUser.email}</p>
              <div className="hero-stats">
                <span><FaProjectDiagram /> {selectedUser.owned_projects || 0} owned</span>
                <span><FaShareAlt /> {selectedUser.shared_projects || 0} shared</span>
              </div>
            </div>
          </div>
          <h3 className="ap-sub-title">Projects</h3>
          {userProjectsLoading ? (
            <div className="loading-state"><FaSpinner className="spinner" /><p>Loading projects...</p></div>
          ) : (
            <div className="item-list">
              {userProjects.length > 0 ? userProjects.map(p => (
                <div key={p.id} className="item-row">
                  <div className="proj-av"><FaProjectDiagram /></div>
                  <div className="item-info">
                    <div className="item-name">{p.project_name}</div>
                    <div className="item-sub">DB: {p.database_name || 'N/A'} · Uploaded: {formatUploadTime(p.created_at)}</div>
                  </div>
                  <div className="item-stats">
                    <span className={`role-badge ${p.role === 'owner' ? 'role-owner' : 'role-shared'}`}>
                      {p.role === 'owner' ? 'Owner' : 'Shared'}
                    </span>
                  </div>
                </div>
              )) : <div className="empty-msg">No projects found</div>}
            </div>
          )}
        </div>
      )}

      {/* VIEW: PROJECT */}
      {view === 'project' && (
        <div className="ap-body">
          <button className="btn-primary" onClick={goBack}><FaArrowLeft /> Back to Projects</button>
          <div className="detail-hero">
            <div className="proj-av proj-av-lg"><FaProjectDiagram /></div>
            <div>
              <h2 className="ap-section-title" style={{ margin: 0 }}>{selectedProject.project_name}</h2>
              <p className="org-row-domain">DB: {selectedProject.database_name || 'N/A'} · Owner: {selectedProject.owner || 'N/A'} · Uploaded: {formatUploadTime(selectedProject.created_at)}</p>
            </div>
          </div>
          <h3 className="ap-sub-title">Users with Access</h3>
          {projectUsersLoading ? (
            <div className="loading-state"><FaSpinner className="spinner" /><p>Loading users...</p></div>
          ) : (
            <div className="item-list">
              {projectUsers.length > 0 ? projectUsers.map(u => (
                <div key={u.email} className="item-row">
                  <div className="user-av">{(u.full_name?.[0] || u.email?.[0] || 'U').toUpperCase()}</div>
                  <div className="item-info">
                    <div className="item-name">{u.full_name || 'Unknown'}</div>
                    <div className="item-sub"><FaEnvelope /> {u.email}</div>
                  </div>
                  <div className="item-stats">
                    <span className={`role-badge ${u.role === 'owner' ? 'role-owner' : 'role-shared'}`}>
                      {u.role === 'owner' ? 'Owner' : 'Shared'}
                    </span>
                  </div>
                </div>
              )) : <div className="empty-msg">No users found</div>}
            </div>
          )}
        </div>
      )}

      {/* ── ADD ORG MODAL ── */}
      {showAddModal && (
        <div className="emodal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="emodal-content emodal-large" onClick={e => e.stopPropagation()}>
            <div className="emodal-header">
              <h3><FaPlus /> Add Organization</h3>
              <button className="emodal-close" onClick={() => setShowAddModal(false)}><FaTimes /></button>
            </div>
            <div className="emodal-body">
              <div className="emodal-section-title"><span>Basic Info</span></div>
              <div className="form-row">
                <div className="form-group">
                  <label>Organization Name *</label>
                  <input type="text" value={newOrg.name} onChange={e => setNewOrg({ ...newOrg, name: e.target.value })} placeholder="e.g. Excavate Research" />
                </div>
                <div className="form-group">
                  <label>Email Domain *</label>
                  <input type="text" value={newOrg.domain} onChange={e => setNewOrg({ ...newOrg, domain: e.target.value })} placeholder="e.g. excavateresearch.com" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Logo URL</label>
                  <input type="url" value={newOrg.logo_url} onChange={e => setNewOrg({ ...newOrg, logo_url: e.target.value })} placeholder="https://..." />
                </div>
                <div className="form-group">
                  <label>Favicon URL</label>
                  <input type="url" value={newOrg.favicon_url} onChange={e => setNewOrg({ ...newOrg, favicon_url: e.target.value })} placeholder="https://..." />
                </div>
              </div>
              <div className="emodal-section-title"><span>Main Features</span></div>
              <div className="features-grid">
                {FEATURE_KEYS.map(key => (
                  <div key={key} className="feature-toggle" onClick={() => toggleMainFeature(key, setNewOrg)}>
                    <span className="feature-name">{key}</span>
                    {newOrg.features[key] ? <FaToggleOn className="toggle-on" /> : <FaToggleOff className="toggle-off" />}
                  </div>
                ))}
              </div>
              <div className="emodal-section-title">
                <span>Advanced Features</span>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button type="button" className="btn-secondary btn-small" onClick={() => setShowScriptingModal(true)}><FaCode /> Scripting Features</button>
                  <button type="button" className="btn-secondary btn-small" onClick={() => setShowDataTablesModal(true)}><FaDatabase /> Data Tables Features</button>
                </div>
              </div>

              <div className="emodal-section-title"><span>Survey Script Downloads</span></div>
              <div className="features-grid-3col">
                {DOWNLOAD_FEATURE_OPTIONS.map(key => (
                  <div key={key} className="feature-toggle" onClick={() => toggleExport(key, 'download_features', setNewOrg)}>
                    <span className="feature-name">{key.replace(/_/g, ' ')}</span>
                    {newOrg.download_features.includes(key) ? <FaToggleOn className="toggle-on" /> : <FaToggleOff className="toggle-off" />}
                  </div>
                ))}
              </div>

              <div className="emodal-section-title"><span>DataTables Export Formats</span></div>
              <div className="features-grid-3col">
                {DATATABLES_EXPORT_OPTIONS.map(key => (
                  <div key={key} className="feature-toggle" onClick={() => toggleExport(key, 'datatables_exports', setNewOrg)}>
                    <span className="feature-name">{key}</span>
                    {newOrg.datatables_exports.includes(key) ? <FaToggleOn className="toggle-on" /> : <FaToggleOff className="toggle-off" />}
                  </div>
                ))}
              </div>

            </div>
            <div className="emodal-footer">
              <button className="btn-secondary" onClick={() => setShowAddModal(false)}>Cancel</button>
              <button className="btn-primary" onClick={handleAdd} disabled={submitting}>
                {submitting ? <FaSpinner className="spinner-small" /> : 'Create Organization'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── EDIT ORG MODAL ── */}
      {showEditModal && (
        <div className="emodal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="emodal-content emodal-large" onClick={e => e.stopPropagation()}>
            <div className="emodal-header">
              <h3><FaEdit /> Edit Organization</h3>
              <button className="emodal-close" onClick={() => setShowEditModal(false)}><FaTimes /></button>
            </div>
            <div className="emodal-body">
              <div className="emodal-section-title"><span>Basic Info</span></div>
              <div className="form-row">
                <div className="form-group">
                  <label>Organization Name *</label>
                  <input type="text" value={editOrg.name} onChange={e => setEditOrg({ ...editOrg, name: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Email Domain *</label>
                  <input type="text" value={editOrg.domain} onChange={e => setEditOrg({ ...editOrg, domain: e.target.value })} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Logo URL</label>
                  <input type="url" value={editOrg.logo_url} onChange={e => setEditOrg({ ...editOrg, logo_url: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Favicon URL</label>
                  <input type="url" value={editOrg.favicon_url} onChange={e => setEditOrg({ ...editOrg, favicon_url: e.target.value })} />
                </div>
              </div>
              <div className="emodal-section-title"><span>Main Features</span></div>
              <div className="features-grid">
                {FEATURE_KEYS.map(key => (
                  <div key={key} className="feature-toggle" onClick={() => toggleMainFeature(key, setEditOrg)}>
                    <span className="feature-name">{key}</span>
                    {editOrg.features[key] ? <FaToggleOn className="toggle-on" /> : <FaToggleOff className="toggle-off" />}
                  </div>
                ))}
              </div>
              <div className="emodal-section-title">
                <span>Advanced Features</span>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button type="button" className="btn-secondary btn-small" onClick={() => setShowScriptingModal(true)}>
                    <FaCode /> Scripting ({Object.values(editOrg.scripting_feature || {}).filter(Boolean).length} on)
                  </button>
                  <button type="button" className="btn-secondary btn-small" onClick={() => setShowDataTablesModal(true)}>
                    <FaDatabase /> Data Tables ({Object.values(editOrg.data_tables_feature || {}).filter(Boolean).length} on)
                  </button>
                </div>
              </div>

              <div className="emodal-section-title"><span>Survey Script Downloads</span></div>
              <div className="features-grid-3col">
                {DOWNLOAD_FEATURE_OPTIONS.map(key => (
                  <div key={key} className="feature-toggle" onClick={() => toggleExport(key, 'download_features', setEditOrg)}>
                    <span className="feature-name">{key.replace(/_/g, ' ')}</span>
                    {(editOrg.download_features || []).includes(key) ? <FaToggleOn className="toggle-on" /> : <FaToggleOff className="toggle-off" />}
                  </div>
                ))}
              </div>

              <div className="emodal-section-title"><span>DataTables Export Formats</span></div>
              <div className="features-grid-3col">
                {DATATABLES_EXPORT_OPTIONS.map(key => (
                  <div key={key} className="feature-toggle" onClick={() => toggleExport(key, 'datatables_exports', setEditOrg)}>
                    <span className="feature-name">{key}</span>
                    {(editOrg.datatables_exports || []).includes(key) ? <FaToggleOn className="toggle-on" /> : <FaToggleOff className="toggle-off" />}
                  </div>
                ))}
              </div>
            </div>
            <div className="emodal-footer">
              <button className="btn-secondary" onClick={() => setShowEditModal(false)}>Cancel</button>
              <button className="btn-primary" onClick={handleEdit} disabled={submitting}>
                {submitting ? <FaSpinner className="spinner-small" /> : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── ADD CUSTOM USER MODAL ── */}
      {showCustomUserModal && (
        <div className="emodal-overlay" onClick={() => { setShowCustomUserModal(false); resetCustomUserForm(); }}>
          <div className="emodal-content emodal-large" onClick={e => e.stopPropagation()}>
            <div className="emodal-header">
              <h3><FaUserPlus /> Add Custom User</h3>
              <button className="emodal-close" onClick={() => { setShowCustomUserModal(false); resetCustomUserForm(); }}><FaTimes /></button>
            </div>

            {customUserSuccess ? (
              <div className="cu-success">
                <FaCheckCircle />
                <p>Custom user created successfully!</p>
              </div>
            ) : (
              <>
                <div className="emodal-body">
                  {customUserErrors.api && <div className="error-banner">{customUserErrors.api}</div>}

                  <div className="emodal-section-title"><span>Credentials</span></div>

                  {/* Username */}
                  <div className="form-group">
                    <label><FaUser style={{ marginRight: 5 }} />Email *</label>
                    <input
                      type="email"
                      value={customUserForm.username}
                      onChange={e => { setCustomUserForm(p => ({ ...p, username: e.target.value })); setCustomUserErrors(p => ({ ...p, username: undefined })); }}
                      placeholder="e.g. user@example.com"
                      className={customUserErrors.username ? 'input-error' : ''}
                    />
                    {customUserErrors.username && <span className="field-error">{customUserErrors.username}</span>}
                  </div>

                  {/* Password */}
                  <div className="form-row">
                    <div className="form-group">
                      <label><FaLock style={{ marginRight: 5 }} />Password *</label>
                      <div className="input-password-wrap">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={customUserForm.password}
                          onChange={e => { setCustomUserForm(p => ({ ...p, password: e.target.value })); setCustomUserErrors(p => ({ ...p, password: undefined })); }}
                          placeholder="Min 6 characters"
                          className={customUserErrors.password ? 'input-error' : ''}
                        />
                        <button type="button" className="btn-eye" onClick={() => setShowPassword(v => !v)}>
                          {showPassword ? <FaEyeSlash /> : <FaEye />}
                        </button>
                      </div>
                      {customUserErrors.password && <span className="field-error">{customUserErrors.password}</span>}
                    </div>
                    <div className="form-group">
                      <label><FaLock style={{ marginRight: 5 }} />Confirm Password *</label>
                      <div className="input-password-wrap">
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          value={customUserForm.confirmPassword}
                          onChange={e => { setCustomUserForm(p => ({ ...p, confirmPassword: e.target.value })); setCustomUserErrors(p => ({ ...p, confirmPassword: undefined })); }}
                          placeholder="Re-enter password"
                          className={customUserErrors.confirmPassword ? 'input-error' : ''}
                        />
                        <button type="button" className="btn-eye" onClick={() => setShowConfirmPassword(v => !v)}>
                          {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                        </button>
                      </div>
                      {customUserErrors.confirmPassword && <span className="field-error">{customUserErrors.confirmPassword}</span>}
                    </div>
                  </div>

                  {/* Org (read-only) */}
                  <div className="form-group">
                    <label><FaBuilding style={{ marginRight: 5 }} />Organization</label>
                    <input type="text" value={selectedOrg?.name || ''} disabled className="input-disabled" />
                  </div>

                  <div className="emodal-section-title"><span>Project Access</span></div>
                  {customUserErrors.projects && <div className="error-banner">{customUserErrors.projects}</div>}
                  <ProjectMultiSelect
                    projects={customUserForm.projects}
                    setProjects={(newProjects) => setCustomUserForm(p => ({ ...p, projects: newProjects }))}
                    availableProjects={orgProjects}
                    loading={orgProjectsLoading}
                  />
                </div>
                <div className="emodal-footer">
                  <button className="btn-secondary" onClick={() => { setShowCustomUserModal(false); resetCustomUserForm(); }}>Cancel</button>
                  <button className="btn-primary" onClick={handleAddCustomUser} disabled={submitting}>
                    {submitting ? <FaSpinner className="spinner-small" /> : <><FaUserPlus /> Create User</>}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── EDIT CUSTOM USER MODAL ── */}
      {showEditCustomUserModal && editingCustomUser && (
        <div className="emodal-overlay" onClick={() => { setShowEditCustomUserModal(false); setEditingCustomUser(null); setCustomUserErrors({}); }}>
          <div className="emodal-content emodal-large" onClick={e => e.stopPropagation()}>
            <div className="emodal-header">
              <h3><FaEdit /> Edit Custom User</h3>
              <button className="emodal-close" onClick={() => { setShowEditCustomUserModal(false); setEditingCustomUser(null); setCustomUserErrors({}); }}><FaTimes /></button>
            </div>
            <div className="emodal-body">
              {customUserErrors.api && <div className="error-banner">{customUserErrors.api}</div>}

              <div className="emodal-section-title"><span>Credentials</span></div>
              <div className="form-group">
                <label><FaUser style={{ marginRight: 5 }} />Email *</label>
                <input
                  type="email"
                  value={editingCustomUser.username}
                  onChange={e => { setEditingCustomUser(p => ({ ...p, username: e.target.value })); setCustomUserErrors(p => ({ ...p, username: undefined })); }}
                  className={customUserErrors.username ? 'input-error' : ''}
                  placeholder="user@example.com"
                />
                {customUserErrors.username && <span className="field-error">{customUserErrors.username}</span>}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label><FaLock style={{ marginRight: 5 }} />New Password <span style={{ fontWeight: 400, color: 'var(--color-text-muted)' }}>(leave blank to keep)</span></label>
                  <div className="input-password-wrap">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={editingCustomUser.newPassword}
                      onChange={e => { setEditingCustomUser(p => ({ ...p, newPassword: e.target.value })); setCustomUserErrors(p => ({ ...p, newPassword: undefined })); }}
                      placeholder="New password"
                      className={customUserErrors.newPassword ? 'input-error' : ''}
                    />
                    <button type="button" className="btn-eye" onClick={() => setShowPassword(v => !v)}>
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                  {customUserErrors.newPassword && <span className="field-error">{customUserErrors.newPassword}</span>}
                </div>
                <div className="form-group">
                  <label><FaLock style={{ marginRight: 5 }} />Confirm Password</label>
                  <div className="input-password-wrap">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={editingCustomUser.confirmPassword}
                      onChange={e => { setEditingCustomUser(p => ({ ...p, confirmPassword: e.target.value })); setCustomUserErrors(p => ({ ...p, confirmPassword: undefined })); }}
                      placeholder="Confirm new password"
                      className={customUserErrors.confirmPassword ? 'input-error' : ''}
                    />
                    <button type="button" className="btn-eye" onClick={() => setShowConfirmPassword(v => !v)}>
                      {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                  {customUserErrors.confirmPassword && <span className="field-error">{customUserErrors.confirmPassword}</span>}
                </div>
              </div>

              <div className="emodal-section-title"><span>Project Access</span></div>
              {customUserErrors.projects && <div className="error-banner">{customUserErrors.projects}</div>}
              <ProjectMultiSelect
                projects={editingCustomUser.projects || []}
                setProjects={(newProjects) => setEditingCustomUser(p => ({ ...p, projects: newProjects }))}
                availableProjects={orgProjects}
                loading={orgProjectsLoading}
              />
            </div>
            <div className="emodal-footer">
              <button className="btn-secondary" onClick={() => { setShowEditCustomUserModal(false); setEditingCustomUser(null); setCustomUserErrors({}); }}>Cancel</button>
              <button className="btn-primary" onClick={handleEditCustomUser} disabled={submitting}>
                {submitting ? <FaSpinner className="spinner-small" /> : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scripting Features Modal */}
      <ScriptingFeaturesModal
        isOpen={showScriptingModal}
        onClose={() => setShowScriptingModal(false)}
        features={showAddModal ? newOrg.scripting_feature : editOrg.scripting_feature}
        toggleFn={(key) => showAddModal ? toggleScriptingFeature(key, setNewOrg) : toggleScriptingFeature(key, setEditOrg)}
        title={showAddModal ? 'Add Organization' : 'Edit Organization'}
      />

      {/* Data Tables Features Modal */}
      <DataTablesFeaturesModal
        isOpen={showDataTablesModal}
        onClose={() => setShowDataTablesModal(false)}
        features={showAddModal ? newOrg.data_tables_feature : editOrg.data_tables_feature}
        toggleFn={(key) => showAddModal ? toggleDataTablesFeature(key, setNewOrg) : toggleDataTablesFeature(key, setEditOrg)}
        title={showAddModal ? 'Add Organization' : 'Edit Organization'}
      />
    </div>
  );
};

export default AdminPanel;